import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api';

const InspectionPanel = ({ map, isInspectionMode, onToggleInspectionMode }) => {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [clickLocation, setClickLocation] = useState(null);
  const [inspectionHistory, setInspectionHistory] = useState([]);
  const [highlightLayerIds, setHighlightLayerIds] = useState([]);

  useEffect(() => {
    if (!map) return;

    const handleMapClick = async (e) => {
      if (!isInspectionMode) return;

      const location = {
        lng: e.lngLat.lng.toFixed(6),
        lat: e.lngLat.lat.toFixed(6)
      };

      setClickLocation(location);

      // Query all visible features at the click point
      const features = map.queryRenderedFeatures(e.point, {
        layers: getVectorLayerIds()
      });

      // Also check for raster layers
      const rasterLayers = getRasterLayerIds();
      let rasterInfo = null;
      
      if (rasterLayers.length > 0) {
        // For raster layers, we can query the rendered features but they won't have properties
        // Instead, we'll indicate that there's a raster layer at this location
        const rasterFeatures = map.queryRenderedFeatures(e.point, {
          layers: rasterLayers
        });
        
        if (rasterFeatures.length > 0) {
          const layerId = rasterFeatures[0].layer.id;
          const datasetId = layerId.replace('layer-', '');
          
          try {
            // Query pixel values from backend
            const response = await fetch(`${API_CONFIG.BASE_URL}/data/datasets/${datasetId}/pixel-value?lng=${e.lngLat.lng}&lat=${e.lngLat.lat}`);
            const pixelData = await response.json();
            
            if (pixelData.success) {
              rasterInfo = {
                layerId: layerId,
                layerType: 'raster',
                datasetId: datasetId,
                pixelValues: pixelData.values,
                bands: pixelData.bands,
                isRGB: pixelData.bands >= 3
              };
            } else {
              rasterInfo = {
                layerId: layerId,
                layerType: 'raster',
                note: 'Could not retrieve pixel values'
              };
            }
          } catch (error) {
            console.error('Error querying pixel values:', error);
            rasterInfo = {
              layerId: layerId,
              layerType: 'raster',
              note: 'Error retrieving pixel values'
            };
          }
        }
      }

      if (features.length > 0) {
        const feature = features[0]; // Get the top-most feature
        const featureInfo = {
          id: Date.now(),
          location,
          layerId: feature.layer.id,
          layerType: 'vector',
          properties: feature.properties,
          geometry: {
            type: feature.geometry.type,
            coordinates: feature.geometry.coordinates
          },
          timestamp: new Date().toLocaleTimeString(),
          rasterInfo: rasterInfo
        };

        setSelectedFeature(featureInfo);
        highlightFeature(feature);
        
        // Add to history (keep last 10 inspections)
        setInspectionHistory(prev => [featureInfo, ...prev.slice(0, 9)]);
      } else if (rasterInfo) {
        // Only raster layer found
        clearAllHighlights();
        setSelectedFeature({
          id: Date.now(),
          location,
          layerId: rasterInfo.layerId,
          layerType: 'raster',
          properties: {
            'Layer Type': 'Raster',
            'Note': rasterInfo.note
          },
          geometry: null,
          timestamp: new Date().toLocaleTimeString()
        });
      } else {
        // No features found at click location
        clearAllHighlights();
        setSelectedFeature({
          id: Date.now(),
          location,
          layerId: null,
          layerType: null,
          properties: {},
          geometry: null,
          timestamp: new Date().toLocaleTimeString(),
          noFeature: true
        });
      }
    };

    const handleMapCursor = () => {
      if (isInspectionMode) {
        map.getCanvas().style.cursor = 'crosshair';
      } else {
        map.getCanvas().style.cursor = 'grab';
      }
    };

    map.on('click', handleMapClick);
    map.on('mouseenter', handleMapCursor);
    map.on('mouseleave', handleMapCursor);

    // Set initial cursor
    handleMapCursor();

    return () => {
      map.off('click', handleMapClick);
      map.off('mouseenter', handleMapCursor);
      map.off('mouseleave', handleMapCursor);
      map.getCanvas().style.cursor = 'grab';
    };
  }, [map, isInspectionMode]);

  // Cleanup highlight when component unmounts or inspection mode changes
  useEffect(() => {
    if (!isInspectionMode) {
      clearAllHighlights();
    }
  }, [isInspectionMode]);

  const highlightFeature = (feature) => {
    clearAllHighlights();
    
    if (!map || !feature) return;

    const highlightId = `highlight-${Date.now()}`;
    setHighlightLayerIds([highlightId]);

    // Add highlight source
    map.addSource(highlightId, {
      type: 'geojson',
      data: feature
    });

    // Add highlight layer based on geometry type
    const geometryType = feature.geometry.type.toLowerCase();
    
    if (geometryType.includes('polygon')) {
      map.addLayer({
        id: highlightId,
        type: 'line',
        source: highlightId,
        paint: {
          'line-color': '#ff0000',
          'line-width': 3,
          'line-opacity': 0.8
        }
      });
    } else if (geometryType.includes('line')) {
      map.addLayer({
        id: highlightId,
        type: 'line',
        source: highlightId,
        paint: {
          'line-color': '#ff0000',
          'line-width': 4,
          'line-opacity': 0.9
        }
      });
    } else if (geometryType.includes('point')) {
      map.addLayer({
        id: highlightId,
        type: 'circle',
        source: highlightId,
        paint: {
          'circle-radius': 8,
          'circle-color': '#ff0000',
          'circle-opacity': 0.8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
    }
  };

  const clearAllHighlights = () => {
    if (!map) return;

    // Clear all highlight layers that we've tracked
    highlightLayerIds.forEach(layerId => {
      try {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
        if (map.getSource(layerId)) {
          map.removeSource(layerId);
        }
      } catch (error) {
        console.warn('Error clearing highlight layer:', layerId, error);
      }
    });

    // Also clear any highlight layers that might exist (fallback)
    const allLayers = map.getStyle().layers || [];
    allLayers.forEach(layer => {
      if (layer.id.startsWith('highlight-')) {
        try {
          if (map.getLayer(layer.id)) {
            map.removeLayer(layer.id);
          }
          if (map.getSource(layer.id)) {
            map.removeSource(layer.id);
          }
        } catch (error) {
          console.warn('Error clearing highlight layer:', layer.id, error);
        }
      }
    });
    
    setHighlightLayerIds([]);
  };

  const getVectorLayerIds = () => {
    if (!map) return [];
    
    // Get all layers that are vector layers (not raster)
    const layers = map.getStyle().layers || [];
    return layers
      .filter(layer => 
        layer.type !== 'raster' && 
        layer.id.startsWith('layer-') &&
        !layer.id.includes('-fill') &&
        !layer.id.includes('-stroke')
      )
      .map(layer => layer.id);
  };

  const getRasterLayerIds = () => {
    if (!map) return [];
    
    // Get all raster layers
    const layers = map.getStyle().layers || [];
    return layers
      .filter(layer => 
        layer.type === 'raster' && 
        layer.id.startsWith('layer-')
      )
      .map(layer => layer.id);
  };

  const clearHistory = () => {
    setInspectionHistory([]);
    setSelectedFeature(null);
    setClickLocation(null);
    clearAllHighlights();
  };

  const formatPropertyValue = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  };

  const getLayerName = (layerId) => {
    if (!layerId) return 'Unknown Layer';
    // Extract dataset ID from layer ID (format: layer-{datasetId})
    const match = layerId.match(/^layer-(.+)$/);
    return match ? `Layer ${match[1].substring(0, 8)}...` : layerId;
  };

  return (
    <div className="inspection-panel">
      <div className="inspection-header">
        <h3>🔍 Feature Inspection</h3>
        <div className="inspection-tools">
          <button
            className={`btn-inspection-toggle ${isInspectionMode ? 'active' : ''}`}
            onClick={onToggleInspectionMode}
            title={isInspectionMode ? 'Disable inspection mode' : 'Enable inspection mode'}
          >
            {isInspectionMode ? '🔍 ON' : '🔍 OFF'}
          </button>
          {inspectionHistory.length > 0 && (
            <button
              className="btn-clear-history"
              onClick={clearHistory}
              title="Clear inspection history"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      <div className="inspection-content">
        {!isInspectionMode && (
          <div className="inspection-instructions">
            <p>Click the inspection tool button above, then click on map features to view their attributes.</p>
          </div>
        )}

        {isInspectionMode && !selectedFeature && (
          <div className="inspection-instructions">
            <p>Inspection mode is active. Click on any feature on the map to view its properties.</p>
          </div>
        )}

        {selectedFeature && (
          <div className="inspection-result">
            <div className="result-header">
              <h4>Selected Feature</h4>
              <span className="result-timestamp">{selectedFeature.timestamp}</span>
            </div>

            <div className="result-location">
              <strong>Location:</strong> {selectedFeature.location.lng}, {selectedFeature.location.lat}
            </div>

            {selectedFeature.noFeature ? (
              <div className="no-feature-message">
                <p>No features found at this location.</p>
              </div>
            ) : (
              <>
                <div className="result-layer">
                  <strong>Layer:</strong> {getLayerName(selectedFeature.layerId)}
                </div>

                {selectedFeature.layerType === 'vector' && (
                  <div className="result-geometry">
                    <strong>Geometry:</strong> {selectedFeature.geometry?.type || 'Unknown'}
                  </div>
                )}

                {selectedFeature.layerType === 'raster' && (
                  <div className="result-layer-type">
                    <strong>Layer Type:</strong> Raster
                  </div>
                )}

                {selectedFeature.rasterInfo && (
                  <div className="result-additional-info">
                    <div className="additional-layer">
                      <strong>Additional Layer:</strong> {getLayerName(selectedFeature.rasterInfo.layerId)} (Raster)
                    </div>
                    {selectedFeature.rasterInfo.pixelValues && (
                      <div className="pixel-values">
                        <strong>Pixel Values:</strong>
                        {selectedFeature.rasterInfo.isRGB ? (
                          <div className="rgb-values">
                            <span className="rgb-component red">R: {selectedFeature.rasterInfo.pixelValues[0] || 'N/A'}</span>
                            <span className="rgb-component green">G: {selectedFeature.rasterInfo.pixelValues[1] || 'N/A'}</span>
                            <span className="rgb-component blue">B: {selectedFeature.rasterInfo.pixelValues[2] || 'N/A'}</span>
                            {selectedFeature.rasterInfo.pixelValues[3] !== undefined && (
                              <span className="rgb-component alpha">A: {selectedFeature.rasterInfo.pixelValues[3]}</span>
                            )}
                          </div>
                        ) : (
                          <span className="single-value">{selectedFeature.rasterInfo.pixelValues[0] || 'N/A'}</span>
                        )}
                      </div>
                    )}
                    {selectedFeature.rasterInfo.note && (
                      <div className="raster-note">
                        <em>{selectedFeature.rasterInfo.note}</em>
                      </div>
                    )}
                  </div>
                )}

                <div className="result-properties">
                  <h5>Properties</h5>
                  {Object.keys(selectedFeature.properties).length === 0 ? (
                    <p className="no-properties">No properties available</p>
                  ) : (
                    <div className="properties-table">
                      {Object.entries(selectedFeature.properties).map(([key, value]) => (
                        <div key={key} className="property-row">
                          <div className="property-key">{key}</div>
                          <div className="property-value">{formatPropertyValue(value)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {inspectionHistory.length > 0 && (
          <div className="inspection-history">
            <h4>Recent Inspections</h4>
            <div className="history-list">
              {inspectionHistory.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`history-item ${selectedFeature?.id === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedFeature(item);
                    // Re-highlight the feature if it has geometry
                    if (item.geometry && item.layerType === 'vector') {
                      const feature = {
                        type: 'Feature',
                        geometry: item.geometry,
                        properties: item.properties
                      };
                      highlightFeature(feature);
                    } else {
                      clearAllHighlights();
                    }
                  }}
                >
                  <div className="history-header">
                    <span className="history-layer">{getLayerName(item.layerId)}</span>
                    <span className="history-time">{item.timestamp}</span>
                  </div>
                  <div className="history-location">
                    {item.location.lng}, {item.location.lat}
                  </div>
                  {item.noFeature && (
                    <div className="history-no-feature">No feature</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionPanel;